"use client";
import React, { useState } from "react";
import { MobileHeader } from "../MobileHeader";
import { TabBar } from "../TabBar";
import { Link, useNavigate } from "react-router-dom";
import ToastNotification from "../ToastNotification";

interface WaterPlan {
  id: string;
  name: string;
  volume: string;
  price: string;
  features: string[];
  recommended?: boolean;
  current?: boolean;
}

const ChangePlanScreen: React.FC = () => {
  const navigate = useNavigate();
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [showPlanComparison, setShowPlanComparison] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showAddressModal, setShowAddressModal] = useState<boolean>(false);
  const [toast, setToast] = useState<{type: 'success' | 'info' | 'warning' | 'error', message: string} | null>(null);
  const [plans] = useState<WaterPlan[]>([
    {
      id: "basic",
      name: "Basic Plan",
      volume: "10,000L",
      price: "R850",
      features: [
        "Up to 10,000L monthly water allocation",
        "Basic leak detection alerts",
        "Monthly usage reports",
        "Email support"
      ]
    },
    {
      id: "standard",
      name: "Standard Plan",
      volume: "18,000L",
      price: "R1,200",
      features: [
        "Up to 18,000L monthly water allocation",
        "Advanced leak detection alerts",
        "Weekly usage reports",
        "Water-saving recommendations",
        "Priority email support"
      ],
      current: true
    },
    {
      id: "premium",
      name: "Premium Plan",
      volume: "25,000L",
      price: "R1,600",
      features: [
        "Up to 25,000L monthly water allocation",
        "Real-time leak detection alerts",
        "Daily usage reports & insights",
        "Personalized water-saving tips",
        "24/7 phone & email support",
        "Priority service response"
      ],
      recommended: true
    },
    {
      id: "business",
      name: "Business Plan",
      volume: "50,000L",
      price: "R3,200",
      features: [
        "Up to 50,000L monthly water allocation",
        "Multiple property management",
        "Advanced analytics dashboard",
        "Dedicated account manager",
        "Custom usage reports",
        "Water efficiency consultation"
      ]
    }
  ]);
  
  const [selectedPlan, setSelectedPlan] = useState<string>(
    plans.find(plan => plan.current)?.id || ""
  );
  
  return (
    <main className="pb-72 mx-auto w-full max-w-[480px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-8 pb-12 w-full">
          <header className="w-full text-2xl font-semibold leading-none text-gray-900">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="flex justify-between items-center">
                  <h1 className="w-full">
                    Change Plan
                  </h1>
                  <Link 
                    to="/profile" 
                    className="text-sm font-medium text-gray-500"
                  >
                    Cancel
                  </Link>
                </div>
              </div>
            </div>
          </header>

          <section className="mt-6 w-full">
            <div className="px-4 w-full">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 mb-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-full flex-shrink-0">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 5V10M10 15H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#1E40AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-blue-800">Current Billing Cycle</h3>
                    <p className="mt-1 text-sm text-blue-700">Changing your plan will take effect immediately. You'll be charged a prorated amount for the remainder of your current billing cycle.</p>
                  </div>
                </div>
              </div>
              
              {/* Plans Selection */}
              <div className="space-y-4 mb-8">
                {plans.map(plan => (
                  <div 
                    key={plan.id}
                    className={`border rounded-xl overflow-hidden transition-all cursor-pointer ${
                      selectedPlan === plan.id 
                        ? 'border-blue-500 shadow-md' 
                        : 'border-gray-200'
                    } ${plan.recommended ? 'relative' : ''}`}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    {plan.recommended && (
                      <div className="absolute top-0 right-0">
                        <div className="bg-green-500 text-white text-xs font-bold py-1 px-3 rounded-bl-lg">
                          RECOMMENDED
                        </div>
                      </div>
                    )}
                    
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="pt-1">
                          <input
                            type="radio"
                            id={plan.id}
                            name="waterPlan"
                            value={plan.id}
                            checked={selectedPlan === plan.id}
                            onChange={(e) => {
                              e.stopPropagation();
                              setSelectedPlan(plan.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                            {plan.current && (
                              <span className="text-xs font-medium bg-blue-100 text-blue-700 py-1 px-2 rounded">
                                CURRENT PLAN
                              </span>
                            )}
                          </div>
                          
                          <div className="mt-1 flex items-baseline">
                            <span className="text-2xl font-bold text-gray-900">{plan.price}</span>
                            <span className="ml-1 text-sm text-gray-500">/month</span>
                          </div>
                          
                          <div className="mt-2 flex items-center">
                            <span className="text-sm font-medium text-gray-700">Monthly allocation:</span>
                            <span className="ml-2 text-sm font-bold text-blue-600">{plan.volume}</span>
                          </div>
                          
                          <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Plan includes:</h4>
                            <ul className="space-y-2">
                              {plan.features.map((feature, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span className="text-sm text-gray-600">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Plan Comparison */}
              <div className="mb-8">
                <button 
                  className="w-full py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                  onClick={() => setShowPlanComparison(true)}
                >
                  View Plan Comparison
                </button>
              </div>
              
              {/* Change Plan Button */}
              <button 
                className={`w-full py-3 font-medium rounded-lg shadow-sm ${
                  selectedPlan === plans.find(plan => plan.current)?.id
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 transition-colors'
                }`}
                disabled={selectedPlan === plans.find(plan => plan.current)?.id}
                onClick={() => setShowConfirmModal(true)}
              >
                Confirm Plan Change
              </button>
              
              <p className="mt-4 text-xs text-center text-gray-500">
                By changing your plan, you agree to our <span className="text-blue-600 cursor-pointer" onClick={() => setShowTermsModal(true)}>Terms of Service</span>
              </p>
            </div>
          </section>
        </section>
      </div>

      <TabBar />
      
      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Terms of Service</h2>
              <button onClick={() => setShowTermsModal(false)} className="p-1">
                <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <h3 className="font-semibold text-gray-900 mb-2">1. Introduction</h3>
              <p className="text-sm text-gray-700 mb-4">
                Welcome to AquaSave. By using our service, you agree to these Terms of Service. Please read them carefully.
              </p>
              
              <h3 className="font-semibold text-gray-900 mb-2">2. Service Description</h3>
              <p className="text-sm text-gray-700 mb-4">
                AquaSave provides water management and conservation services. We offer various plans with different features and water allocations. By subscribing to a plan, you agree to abide by the usage limits and conditions specified.
              </p>
              
              <h3 className="font-semibold text-gray-900 mb-2">3. Billing and Subscription</h3>
              <p className="text-sm text-gray-700 mb-4">
                You agree to pay the fees for the plan you select. Fees are charged monthly. When changing plans, you will be charged a prorated amount for the remainder of your current billing cycle. All plan changes take effect immediately upon confirmation.
              </p>
              
              <h3 className="font-semibold text-gray-900 mb-2">4. Water Usage Monitoring</h3>
              <p className="text-sm text-gray-700 mb-4">
                Our service includes water usage monitoring and analysis. You consent to the collection and analysis of your water usage data for service improvement and personalized recommendations.
              </p>
              
              <h3 className="font-semibold text-gray-900 mb-2">5. Changes to Terms</h3>
              <p className="text-sm text-gray-700 mb-4">
                We may modify these terms at any time. We will notify you of significant changes. Your continued use of the service after changes indicates your acceptance of the new terms.
              </p>
            </div>
            <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200">
              <button onClick={() => setShowTermsModal(false)} className="w-full py-3 font-medium bg-blue-600 text-white rounded-lg">
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Plan Comparison Modal */}
      {showPlanComparison && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Plan Comparison</h2>
              <button onClick={() => setShowPlanComparison(false)} className="p-1">
                <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Features</th>
                    {plans.map(plan => (
                      <th key={plan.id} className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex flex-col items-center">
                          <span>{plan.name}</span>
                          <span className="mt-1 text-blue-600 font-bold">{plan.price}/mo</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="py-4 px-4 text-sm text-gray-900 font-medium">Monthly Water Allocation</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="py-4 px-4 text-center text-sm text-gray-900">
                        <span className="font-semibold text-blue-600">{plan.volume}</span>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="py-4 px-4 text-sm text-gray-900 font-medium">Leak Detection</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="py-4 px-4 text-center text-sm text-gray-900">
                        {plan.id === 'basic' && 'Basic'}
                        {plan.id === 'standard' && 'Advanced'}
                        {plan.id === 'premium' && 'Real-time'}
                        {plan.id === 'business' && 'Enterprise'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="py-4 px-4 text-sm text-gray-900 font-medium">Usage Reports</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="py-4 px-4 text-center text-sm text-gray-900">
                        {plan.id === 'basic' && 'Monthly'}
                        {plan.id === 'standard' && 'Weekly'}
                        {plan.id === 'premium' && 'Daily'}
                        {plan.id === 'business' && 'Custom'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="py-4 px-4 text-sm text-gray-900 font-medium">Support</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="py-4 px-4 text-center text-sm text-gray-900">
                        {plan.id === 'basic' && 'Email'}
                        {plan.id === 'standard' && 'Priority Email'}
                        {plan.id === 'premium' && '24/7 Phone & Email'}
                        {plan.id === 'business' && 'Dedicated Account Manager'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 flex justify-end">
              <button onClick={() => setShowPlanComparison(false)} className="px-6 py-2 font-medium bg-blue-600 text-white rounded-lg">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Plan Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Confirm Plan Change</h2>
              <p className="text-gray-700 mb-6">
                You are about to change your plan to <span className="font-semibold text-blue-600">{plans.find(p => p.id === selectedPlan)?.name}</span> at {plans.find(p => p.id === selectedPlan)?.price}/month. This change will take effect immediately.   
              </p>
              <div className="flex space-x-3">
                <button 
                  onClick={() => setShowConfirmModal(false)} 
                  className="flex-1 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    setShowConfirmModal(false);
                    setShowAddressModal(true);
                  }} 
                  className="flex-1 py-2 bg-blue-600 text-white font-medium rounded-lg"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Address Confirmation Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Confirm Billing Address</h2>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="text-gray-900 font-medium">John Doe</p>
                <p className="text-gray-700 mt-1">123 Water Street</p>
                <p className="text-gray-700">Apartment 4B</p>
                <p className="text-gray-700">Cape Town, 8001</p>
                <p className="text-gray-700">South Africa</p>
              </div>
              <div className="flex space-x-3">
                <button 
                  onClick={() => setShowAddressModal(false)} 
                  className="flex-1 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg"
                >
                  Edit
                </button>
                <button 
                  onClick={() => {
                    setShowAddressModal(false);
                    navigate('/topup');
                    // Show success toast after a short delay
                    setTimeout(() => {
                      setToast({ type: 'success', message: 'Plan changed successfully!' });
                    }, 1000);
                  }} 
                  className="flex-1 py-2 bg-blue-600 text-white font-medium rounded-lg"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast notification */}
      {toast && (
        <ToastNotification
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </main>
  );
};

export default ChangePlanScreen;
